
import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CoachMark, Eyebrow, Heading, Row, Spacer, Stack, Text } from '@client/shared/design';
import { useHasRole } from '@client/shared/auth';
import { useTutorial } from '@client/features/account';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { formatCalendarDate } from '@client/shared/lib/wall-clock-intake.ts';
import { PATHS } from '@client/shared/router/paths.ts';
import {
  fetchListingDetails,
  listingDetailsKey,
  detailAddress,
  type ListingDetail,
} from './listing-details-operations.ts';
import { LISTINGS_QUERY_PREFIX } from '@client/features/listings';
import { BILLING_STATUS_QUERY_KEY } from '@client/features/group';
import { runArchiveListing, runUnarchiveListing } from './archive-listing-mutation.ts';
import { ArchiveConfirmPopover } from './ArchiveConfirmPopover.tsx';
import { BookingCalendar } from './BookingCalendar.tsx';
import styles from './ListingDetails.module.css';

const LISTINGS_HREF = PATHS.listings;

export interface ListingDetailsProps {
  listingId?: string;
}

export function listingIdFromPath(pathname: string): string | null {
  const match = /\/listings\/([^/?#]+)/.exec(pathname);
  const id = match?.[1];
  if (!id || id === 'new') return null;
  return decodeURIComponent(id);
}

export function ListingDetails({ listingId }: ListingDetailsProps) {
  const id =
    listingId ??
    (typeof window !== 'undefined' ? listingIdFromPath(window.location.pathname) : null);

  const query = useQuery({
    queryKey: listingDetailsKey(id ?? '∅'),
    queryFn: ({ signal }) => fetchListingDetails(id!, signal),
    enabled: Boolean(id),
  });

  if (!id) {
    return <NotFound />;
  }

  return (
    <Stack gap={6}>
      <a href={LISTINGS_HREF} className={styles.back}>
        <BackGlyph />
        All listings
      </a>

      {query.isPending ? (
        <DetailsSkeleton />
      ) : query.isError ? (
        <DetailsError onRetry={() => void query.refetch()} />
      ) : !query.data.listing ? (
        <NotFound />
      ) : (
        <ListingBody listing={query.data.listing} />
      )}
    </Stack>
  );
}

function ListingBody({ listing }: { listing: ListingDetail }) {
  const address = detailAddress(listing);
  const isAdmin = useHasRole('admin', 'owner');
  const canBlock = isAdmin;
  const canBookForOthers = isAdmin;

  const tutorial = useTutorial();
  const calendarRef = useRef<HTMLDivElement | null>(null);

  const queryClient = useQueryClient();

  const archiveAnchorRef = useRef<HTMLSpanElement | null>(null);
  const [confirming, setConfirming] = useState(false);

  const closeConfirm = useCallback(() => {
    setConfirming(false);
    archiveAnchorRef.current?.querySelector('button')?.focus();
  }, []);

  const archiveMutation = useMutation({
    mutationFn: () => runArchiveListing(listing.id!),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: listingDetailsKey(listing.id!) }),
        queryClient.invalidateQueries({ queryKey: LISTINGS_QUERY_PREFIX }),
        // Archiving frees a cap slot; refresh the status the add-listing affordance keys off.
        queryClient.invalidateQueries({ queryKey: BILLING_STATUS_QUERY_KEY }),
      ]);
      closeConfirm();
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => runUnarchiveListing(listing.id!),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: listingDetailsKey(listing.id!) }),
        queryClient.invalidateQueries({ queryKey: LISTINGS_QUERY_PREFIX }),
        queryClient.invalidateQueries({ queryKey: BILLING_STATUS_QUERY_KEY }),
      ]);
    },
  });

  return (
    <Stack gap={7}>
      <section aria-label="Listing information">
        <Stack gap={4}>
          <Stack gap={1}>
            <Row align="center" gap={3}>
              <Eyebrow>Listing</Eyebrow>
              <Spacer />
              {isAdmin && listing.id && !listing.archivedAt && (
                <Row align="center" gap={2} className={styles.headerActions}>
                  <EditListingLink listingId={listing.id} />
                  <span ref={archiveAnchorRef} className={styles.archiveAnchor}>
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<ArchiveGlyph />}
                      onClick={() => setConfirming(true)}
                    >
                      Archive
                    </Button>
                  </span>
                  {confirming && (
                    <ArchiveConfirmPopover
                      anchor={archiveAnchorRef}
                      busy={archiveMutation.isPending}
                      error={archiveMutation.isError}
                      onConfirm={() => archiveMutation.mutate()}
                      onCancel={closeConfirm}
                    />
                  )}
                </Row>
              )}
            </Row>
            <Heading level={1} className={styles.address}>
              {address}
            </Heading>
          </Stack>

          <Row gap={3} align="center" className={styles.factRow}>
            {listing.archivedAt && (
              <Row gap={2} align="center">
                <span className={styles.archivedTag}>Archived</span>
                {isAdmin && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<UnarchiveGlyph />}
                    loading={unarchiveMutation.isPending}
                    onClick={() => unarchiveMutation.mutate()}
                  >
                    Unarchive
                  </Button>
                )}
                {unarchiveMutation.isError && (
                  <Text tone="danger" size="sm">
                    {messageFor(unarchiveMutation.error, 'Couldn’t unarchive. Try again.')}
                  </Text>
                )}
              </Row>
            )}
            {listing.bookingUntil ? (
              <span className={styles.fact}>
                Bookable through <strong>{formatCalendarDate(listing.bookingUntil)}</strong>
              </span>
            ) : (
              listing.bookingHorizonDays != null && (
                <span className={styles.fact}>
                  Bookable up to{' '}
                  <strong>{listing.bookingHorizonDays} days</strong> out
                </span>
              )
            )}
          </Row>

          {listing.showingNotes && (
            <Card pad="md" spine className={styles.notesCard}>
              <Stack gap={1}>
                <Eyebrow>Showing instructions</Eyebrow>
                <Text className={styles.notes}>{listing.showingNotes}</Text>
              </Stack>
            </Card>
          )}
        </Stack>
      </section>

      {listing.archivedAt ? (
        <ArchivedNotice
          canUnarchive={isAdmin}
          onUnarchive={() => unarchiveMutation.mutate()}
          unarchiving={unarchiveMutation.isPending}
          unarchiveError={unarchiveMutation.isError ? unarchiveMutation.error : null}
        />
      ) : (
        listing.viewerCanBook ? (
          <section aria-label="Book an open house">
            <Stack gap={3}>
              <Stack gap={1}>
                <Heading level={3}>{canBlock ? 'Open-house calendar' : 'Book an open house'}</Heading>
                <Text size="sm" tone="soft">
                  {canBlock
                    ? 'Book a slot, or switch to “Block time” to mark time unavailable. Times are in the listing’s zone.'
                    : 'Claim a 30-minute or longer slot. Times are in the listing’s zone.'}
                </Text>
              </Stack>
              {tutorial.flow && (
                <CoachMark
                  step="2 of 2"
                  dismissLabel={tutorial.flow === 'admin' ? 'Got it' : undefined}
                  anchor={{ ref: calendarRef }}
                  placement="top"
                  onDismiss={tutorial.dismiss}
                >
                  {tutorial.flow === 'admin'
                    ? 'This calendar is where your agents book open houses on this listing. They claim open slots right here, and you can block time to mark it unavailable.'
                    : 'Select an open time on the calendar below, then confirm to book your open house.'}
                </CoachMark>
              )}
              <div ref={calendarRef}>
                <BookingCalendar
                  listing={listing}
                  canBlock={canBlock}
                  canBookForOthers={canBookForOthers}
                  onBooked={tutorial.flow ? tutorial.dismiss : undefined}
                />
              </div>
            </Stack>
          </section>
        ) : (
          <ViewOnlyNotice />
        )
      )}
    </Stack>
  );
}

function ArchivedNotice({
  canUnarchive,
  onUnarchive,
  unarchiving,
  unarchiveError,
}: {
  canUnarchive: boolean;
  onUnarchive: () => void;
  unarchiving: boolean;
  unarchiveError: unknown;
}) {
  return (
    <section aria-label="Archived listing">
      <Card pad="lg" spine className={styles.archivedNotice}>
        <Stack gap={3}>
          <span className={styles.archivedNoticeTag}>
            <ArchiveGlyph />
            Archived
          </span>
          <Stack gap={1}>
            <Heading level={3} as="h2">
              This listing is archived
            </Heading>
            <Text size="sm" tone="soft" className={styles.archivedNoticeCopy}>
              It’s hidden from the listings browse and can’t be booked while archived. Unarchive to
              make it bookable again.
            </Text>
          </Stack>
          {canUnarchive && (
            <Button
              variant="secondary"
              size="sm"
              className={styles.archivedNoticeAction}
              leadingIcon={<UnarchiveGlyph />}
              loading={unarchiving}
              onClick={onUnarchive}
            >
              Unarchive
            </Button>
          )}
          {unarchiveError != null && (
            <Text tone="danger" size="sm">
              {messageFor(unarchiveError, 'Couldn’t unarchive. Try again.')}
            </Text>
          )}
        </Stack>
      </Card>
    </section>
  );
}

function ViewOnlyNotice() {
  return (
    <section aria-label="Booking access">
      <Card pad="lg" spine className={styles.viewOnly}>
        <Stack gap={3}>
          <span className={styles.viewOnlyTag}>
            <LockGlyph />
            View only
          </span>
          <Stack gap={1}>
            <Heading level={3} as="h2">
              You can view this listing, but can’t book it
            </Heading>
            <Text size="sm" tone="soft" className={styles.viewOnlyCopy}>
              You’re not assigned to book open houses on this listing. You can still review the
              details above. Ask your admin if you need booking access.
            </Text>
          </Stack>
        </Stack>
      </Card>
    </section>
  );
}

function NotFound() {
  return (
    <Card pad="lg" className={styles.state}>
      <Stack gap={3} align="center">
        <Heading level={3} as="h2">
          Listing not found
        </Heading>
        <Text size="sm" tone="soft" className={styles.stateCopy}>
          This listing doesn’t exist or isn’t available to your group.
        </Text>
        <a href={LISTINGS_HREF} className={styles.stateLink}>
          Back to listings
        </a>
      </Stack>
    </Card>
  );
}

function DetailsError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card pad="lg" className={styles.state}>
      <Stack gap={3} align="center">
        <Heading level={3} as="h2">
          We couldn’t load this listing
        </Heading>
        <Text size="sm" tone="soft" className={styles.stateCopy}>
          Something went wrong loading this listing. Try again or refresh if it keeps happening.
        </Text>
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </Stack>
    </Card>
  );
}

function DetailsSkeleton() {
  return (
    <Stack gap={6} aria-hidden="true">
      <Stack gap={2}>
        <span className={styles.skBlock} style={{ width: '50%', height: 'var(--space-6)' }} />
        <span className={styles.skBlock} style={{ width: '30%' }} />
      </Stack>
      <Card pad="none" spine className={styles.skCalendar}>
        <span className={styles.skBlock} style={{ width: '100%', height: '12rem' }} />
      </Card>
    </Stack>
  );
}

function EditListingLink({ listingId }: { listingId: string }) {
  return (
    <a href={`/listings/${listingId}/edit`} className={styles.editLink}>
      <PencilGlyph />
      Edit listing
    </a>
  );
}

function PencilGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 20h4l10.5-10.5a2 2 0 0 0 0-2.83l-1.17-1.17a2 2 0 0 0-2.83 0L4 16v4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M13.5 6.5l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function ArchiveGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path d="M10 12h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function UnarchiveGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M12 17v-5m0 0l-2 2m2-2l2 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M8 11V8a4 4 0 0 1 8 0v3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BackGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
